import { jwtDecode } from 'jwt-decode';
import config from '../config';
import { User } from '../types';

interface JWTPayload {
  exp: number;
  user_id: string;
  role: string;
}

export class AuthUtils {
  static isTokenExpired(token: string): boolean {
    try {
      // 仮実装の場合はトークンの有効期限チェックをスキップ
      if (token.length === 64 && /^[a-f0-9]+$/i.test(token)) {
        return false; // 仮実装では常に有効とする
      }
      
      // JWT形式のトークンの場合
      const decoded = jwtDecode<JWTPayload>(token);
      if (!decoded.exp) {
        console.warn('Token has no expiration:', decoded);
        return false;
      }
      const currentTime = Date.now() / 1000;
      const hasExpired = decoded.exp < currentTime;
      
      if (hasExpired) {
        console.warn('Token has expired:', {
          exp: new Date(decoded.exp * 1000),
          now: new Date(),
          decoded
        });
      }
      
      return hasExpired;
    } catch (error) {
      console.warn('Token validation error:', error);
      // トークンのデコードに失敗した場合は、64文字の16進数形式なら有効とする
      if (token.length === 64 && /^[a-f0-9]+$/i.test(token)) {
        return false;
      }
      return true;
    }
  }

  static getToken(): string | null {
    return localStorage.getItem(config.auth.tokenKey);
  }

  static getStoredUser(): User | null {
    const userStr = localStorage.getItem(config.auth.userKey);
    return userStr ? JSON.parse(userStr) : null;
  }

  static clearAuthData(): void {
    localStorage.removeItem(config.auth.tokenKey);
    localStorage.removeItem(config.auth.userKey);
    localStorage.removeItem(config.auth.refreshTokenKey);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(config.auth.refreshTokenKey);
  }

  static setToken(token: string): void {
    localStorage.setItem(config.auth.tokenKey, token);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(config.auth.refreshTokenKey, token);
  }

  static storeAuthData(token: string, refreshToken: string, user: User): void {
    this.setToken(token);
    this.setRefreshToken(refreshToken);
    localStorage.setItem(config.auth.userKey, JSON.stringify(user));
  }

  static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  static getUserRole(): string {
    const user = this.getStoredUser();
    return user?.role || '';
  }

  static hasRole(requiredRole: string): boolean {
    const userRole = this.getUserRole();
    return userRole.toLowerCase() === requiredRole.toLowerCase();
  }

  static hasAnyRole(requiredRoles: string[]): boolean {
    const userRole = this.getUserRole();
    return requiredRoles.some(role => 
      role.toLowerCase() === userRole.toLowerCase()
    );
  }
} 